import { Component, ErrorInfo, ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { AlertTriangle, RefreshCw, Home, ArrowLeft } from "lucide-react";
import { reportError } from "@/lib/errorReporting";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  retryCount: number;
}

class ErrorBoundary extends Component<Props, State> {
  private static MAX_AUTO_RETRIES = 2;

  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null, retryCount: 0 };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("[ErrorBoundary]", error, info.componentStack);
    reportError("critical", error.message, {
      feature: "react_crash",
      detail: info.componentStack?.slice(0, 500) || undefined,
    });

    if (this.state.retryCount < ErrorBoundary.MAX_AUTO_RETRIES) {
      setTimeout(() => {
        this.setState((prev) => ({
          hasError: false,
          error: null,
          retryCount: prev.retryCount + 1,
        }));
      }, 1000 * (this.state.retryCount + 1));
    }
  }

  handleManualRetry = () => {
    this.setState({ hasError: false, error: null, retryCount: 0 });
  };

  handleGoHome = () => {
    window.location.href = "/";
  };

  handleGoBack = () => {
    window.history.back();
  };

  handleFullReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError && this.state.retryCount >= ErrorBoundary.MAX_AUTO_RETRIES) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-background p-6" role="alert" aria-live="assertive">
          <div className="max-w-md w-full text-center space-y-6">
            {/* Icon */}
            <div className="relative mx-auto w-20 h-20">
              <div className="absolute inset-0 rounded-2xl bg-destructive/10 animate-pulse" />
              <div className="relative w-full h-full rounded-2xl bg-destructive/15 flex items-center justify-center">
                <AlertTriangle className="h-9 w-9 text-destructive" />
              </div>
            </div>

            {/* Message */}
            <div className="space-y-2">
              <h1 className="text-xl font-bold font-display">Oj, något gick fel</h1>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Vi försökte reparera automatiskt men det fungerade inte. Prova att ladda om sidan eller gå tillbaka.
              </p>
            </div>

            {/* Actions */}
            <div className="flex flex-col gap-2 max-w-xs mx-auto">
              <Button onClick={this.handleFullReload} className="gap-2 w-full shadow-glow">
                <RefreshCw className="h-4 w-4" /> Ladda om sidan
              </Button>
              <div className="flex gap-2">
                <Button onClick={this.handleGoBack} variant="outline" className="gap-2 flex-1">
                  <ArrowLeft className="h-4 w-4" /> Tillbaka
                </Button>
                <Button onClick={this.handleGoHome} variant="outline" className="gap-2 flex-1">
                  <Home className="h-4 w-4" /> Startsidan
                </Button>
              </div>
              <button
                onClick={this.handleManualRetry}
                className="text-xs text-muted-foreground hover:text-foreground transition-colors pt-1"
              >
                Försök igen utan omladdning
              </button>
            </div>

            {/* Error detail (collapsed) */}
            {this.state.error && (
              <details className="text-left mt-4">
                <summary className="text-xs text-muted-foreground/50 cursor-pointer hover:text-muted-foreground">
                  Teknisk information
                </summary>
                <pre className="mt-2 text-[10px] text-muted-foreground/40 bg-muted/30 rounded-lg p-3 overflow-x-auto max-h-24">
                  {this.state.error.message}
                </pre>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
